var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/index.ts
import { XStorageError as XStorageError2 } from "@byted/tiktok-zti-storage-error";

// src/version.ts
var version_default = "0.1.0";

// src/const.ts
var defaultConfig = () => {
  return {
    dbName: "secure-store",
    storeName: "cryptvalues",
    closeDBTime: 20 * 1e3
  };
};
var DBInstanceMap = {};
var closeTimerMap = {};
var getIDB = () => {
  try {
    return (
      // @ts-ignore
      indexedDB || window["indexedDB"] || // @ts-ignore
      webkitIndexedDB || // @ts-ignore
      mozIndexedDB || // @ts-ignore
      OIndexedDB
    );
  } catch (e) {
    return;
  }
};
var ERROR_NOT_SUPPORT = "DBStorageNotSupport";

// src/task.ts
import { XStorageError } from "@byted/tiktok-zti-storage-error";
var promise = Promise.resolve();
var retryCount = 0;
var createTaskTransaction = (dbName, task, onQuotaErrorCallback) => {
  return promise = promise.then(() => {
    const runTask = () => {
      return task().then((value) => {
        retryCount = 0;
        return value;
      }).catch((e) => {
        if (retryCount < 3) {
          retryCount++;
          DBInstanceMap[dbName] = void 0;
          checkQuotaExceededError(dbName, e).then((res) => {
            if (typeof onQuotaErrorCallback === "function") {
              return onQuotaErrorCallback(res.isQuotaError, res.clean);
            }
          });
          return runTask();
        }
        retryCount = 0;
        throw e;
      });
    };
    return runTask();
  });
};
var checkQuotaExceededError = (dbName, error) => {
  const name = (error == null ? void 0 : error.name) || "";
  const isQuotaError = name.toLowerCase() === "QuotaExceededError".toLowerCase();
  return Promise.resolve({
    isQuotaError,
    clean(targetDBName) {
      const indexedDB2 = getIDB();
      const req = indexedDB2 == null ? void 0 : indexedDB2.deleteDatabase(targetDBName || dbName);
      return new Promise((resolve) => {
        if (req) {
          req.onsuccess = () => {
            resolve();
          };
          req.onerror = () => {
            resolve();
          };
        } else {
          resolve();
        }
      });
    }
  });
};
var getObjectStoreValue = (req) => {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error);
    };
  });
};
var createTransaction = (transaction, req) => {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve();
    };
    transaction.onabort = transaction.onerror = () => {
      var _a;
      const error = req ? req.error || ((_a = req.transaction) == null ? void 0 : _a.error) : { name: "TransactionAbortOrError", message: "" };
      reject(new XStorageError((error == null ? void 0 : error.name) || "TransactionAbortOrError", (error == null ? void 0 : error.message) || ""));
    };
  });
};

// src/index.ts
var _DBStorage = class _DBStorage {
  constructor(options) {
    this.config = defaultConfig();
    /**
     * 获取指定键名的值
     */
    this.getItem = (key) => {
      return this.getItemByKeys([key]).then((res) => {
        return res[0];
      });
    };
    this.getItemByKeys = (keys) => {
      const { storeName } = this.config;
      return this.transaction(() => {
        return this.openDB().then((db) => {
          const objectStore = db.transaction(storeName, "readonly").objectStore(storeName);
          return Promise.all(
            keys.map((key) => {
              return getObjectStoreValue(objectStore.get(key)).then((result) => {
                const { data } = JSON.parse(result || "{}");
                return data;
              });
            })
          );
        });
      });
    };
    /**
     * 设置指定键名和值
     * @example
     * ```js
     * const dbStorage = new DBStorage();
     * dbStorage.setItem('key', 'value').then(console).catch(console.error);
     *
     * ```
     */
    this.setItem = (key, value) => {
      return this.setItemByKeys([[key, value]]).then((res) => {
        return res[0];
      });
    };
    this.setItemByKeys = (pair) => {
      const { storeName } = this.config;
      return this.transaction(() => {
        return this.openDB().then((db) => {
          const transaction = db.transaction(storeName, "readwrite");
          const store = transaction.objectStore(storeName);
          return Promise.all(
            pair.map(([key, value]) => {
              const dataValue = typeof value === "string" ? JSON.stringify({ data: value }) : JSON.stringify({ data: value });
              return getObjectStoreValue(store.get(key)).then((lastValue) => {
                if (lastValue !== dataValue) {
                  store.put(dataValue, key);
                }
                return value;
              });
            })
          ).then((res) => {
            return createTransaction(transaction).then(() => {
              return res;
            });
          });
        });
      });
    };
    /**
     * 移除指定数据
     */
    this.removeItem = (key) => {
      const { storeName } = this.config;
      return this.openDB().then((db) => {
        const transaction = db.transaction(storeName, "readwrite");
        const store = transaction.objectStore(storeName);
        const req = store.delete(key);
        return createTransaction(transaction, req);
      });
    };
    this.getKeys = () => {
      const { storeName } = this.config;
      return this.transaction(() => {
        return this.openDB().then((db) => {
          const transaction = db.transaction(storeName);
          const req = transaction.objectStore(storeName).openKeyCursor();
          return new Promise((resolve, reject) => {
            const keys = [];
            req.onsuccess = () => {
              if (!req.result) {
                resolve(keys);
              } else {
                keys.push(req.result.key);
                req.result.continue();
              }
            };
            req.onerror = () => {
              reject(req.error);
            };
          });
        });
      });
    };
    this.openDB = () => {
      const { storeName, dbName, closeDBTime } = this.config;
      clearTimeout(closeTimerMap[dbName]);
      closeTimerMap[dbName] = setTimeout(() => {
        _DBStorage.closeDB(dbName).catch(() => {
        });
      }, closeDBTime);
      return _DBStorage.openDB(this.config).then((db) => {
        if (!db.objectStoreNames.contains(storeName)) {
          const newVersion = db.version + 1;
          return _DBStorage.closeDB(dbName).then(() => {
            return _DBStorage.openDB(this.config, newVersion);
          });
        }
        return db;
      });
    };
    this.transaction = (task) => {
      const { dbName, onQuotaErrorCallback } = this.config;
      return createTaskTransaction(dbName, task, (error, clean) => {
        if (error) {
          if (typeof onQuotaErrorCallback === "function") {
            onQuotaErrorCallback.call(this, clean);
          }
        }
      });
    };
    this.config = __spreadValues(__spreadValues({}, this.config), options || {});
  }
};
_DBStorage.isSupport = () => {
  return Boolean(getIDB());
};
_DBStorage.openDB = (options, v) => {
  const { dbName, storeName, version: dbVersion } = options;
  if (v) {
    DBInstanceMap[dbName] = void 0;
  }
  if (!_DBStorage.isSupport()) {
    return Promise.reject(new XStorageError2(ERROR_NOT_SUPPORT));
  }
  const idb = getIDB();
  const createIndexedDB = (indexedDBName, indexedDBVersion) => {
    return new Promise((resolve, reject) => {
      const req = idb.open(indexedDBName, indexedDBVersion);
      req.onsuccess = () => {
        resolve(req.result);
      };
      req.onerror = (event) => {
        var _a, _b;
        event.preventDefault();
        reject(new XStorageError2(((_a = req.error) == null ? void 0 : _a.name) || "IndexedDBOpenError", (_b = req.error) == null ? void 0 : _b.message));
      };
      req.onupgradeneeded = (evt) => {
        try {
          (req.result || evt.target.result).createObjectStore(storeName);
        } catch (e) {
          reject(e);
        }
      };
    });
  };
  return DBInstanceMap[dbName] || (DBInstanceMap[dbName] = new Promise((resolve, reject) => {
    try {
      createIndexedDB(dbName, v || dbVersion || 1).catch((err) => {
        if (isVersionError(err)) {
          return getDefaultDBVersion(dbName).then((currentVersion) => {
            if (currentVersion) {
              return createIndexedDB(dbName, currentVersion);
            }
            return createIndexedDB(dbName);
          }).catch(() => {
            return createIndexedDB(dbName);
          });
        }
        throw err;
      }).then(resolve).catch(reject);
    } catch (e) {
      reject(new XStorageError2(ERROR_NOT_SUPPORT, e.message));
    }
  }));
};
_DBStorage.deleteDB = (dbName) => {
  const idb = getIDB();
  if (idb) {
    return _DBStorage.closeDB(dbName).then(() => {
      if (idb == null ? void 0 : idb.deleteDatabase) {
        const req = idb == null ? void 0 : idb.deleteDatabase(dbName);
        if (req) {
          clearTimeout(closeTimerMap[dbName]);
          return new Promise((resolve, reject) => {
            req.onsuccess = () => {
              resolve(req.result);
            };
            req.onerror = () => {
              reject(req == null ? void 0 : req.error);
            };
          });
        }
      }
      return Promise.reject(new XStorageError2(`IDBDeleteDataBaseError`));
    });
  }
  return Promise.reject(new XStorageError2(`IDBObjectError`));
};
_DBStorage.closeDB = (dbName) => {
  return new Promise((resolve, reject) => {
    if (DBInstanceMap[dbName]) {
      DBInstanceMap[dbName].then((db) => {
        const close = () => {
          DBInstanceMap[dbName] = void 0;
          try {
            db.close();
            resolve(1);
          } catch (e) {
            reject(e);
          }
        };
        close();
      }).catch(reject);
    } else {
      resolve(-1);
    }
  });
};
var DBStorage = _DBStorage;
var src_default = DBStorage;
var isVersionError = (err) => {
  if (((err == null ? void 0 : err.name) || "").toString().toLowerCase() !== "versionerror") {
    return false;
  }
  if (((err == null ? void 0 : err.message) || "").indexOf("version") !== -1 || ((err == null ? void 0 : err.message) || "").match(/\s*less\s*than/i) || ((err == null ? void 0 : err.message) || "").match(/\s*higher\s*version\s*than\s*/gi) !== -1) {
    return true;
  }
};
var getDefaultDBVersion = (dbName) => {
  return new Promise((resolve, reject) => {
    const idb = getIDB();
    if (idb == null ? void 0 : idb.databases) {
      return idb.databases().then((list) => {
        const db = list.filter((item) => {
          return item.name === dbName;
        })[0];
        resolve(db == null ? void 0 : db.version);
      }).catch(reject);
    }
    reject(new Error(`idb.database is ${typeof (idb == null ? void 0 : idb.databases)}`));
  });
};
export {
  src_default as default,
  version_default as version
};
